import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { collection, doc, getDocsFromServer, onSnapshot, query, where } from 'firebase/firestore';
import { db } from '../firebase';
import { decryptData, generateEncryptionKey, getLegacyEncryptionPreference, getLegacyStoredEncryptionKey, getStoredEncryptionState, storeEncryptionState } from '../utils/encryption';
import { changeKeyPassword, cryptoKeysEqual, getKeyFromCloud, storeAndVerifyKeyInCloud } from '../utils/cloudKeyStorage';
import { runEncryptionMigration } from '../utils/encryptionMigration';
import { profileHasCloudKey, readEncryptionPolicy } from '../utils/encryptionPolicy';
import { abortEncryptionTransition } from '../firebase/encryptionTransition';
import { logError } from '../utils/logger';

const EncryptionContext = createContext();
const MAX_MIGRATION_NOTES = 499;
const notesFor = async (userId) => (await getDocsFromServer(query(collection(db, 'notes'), where('userId', '==', userId)))).docs
  .map((snapshot) => ({ id: snapshot.id, content: snapshot.data()?.content }));
const operationId = () => {
  const webCrypto = window.crypto;
  if (!webCrypto) throw new Error('Secure random generator unavailable.');
  if (typeof webCrypto?.randomUUID === 'function') return `encryption-${webCrypto.randomUUID()}`;
  const bytes = webCrypto.getRandomValues(new Uint8Array(16));
  return `encryption-${Array.from(bytes, (byte) => byte.toString(16).padStart(2, '0')).join('')}`;
};

export const useEncryption = () => {
  const context = useContext(EncryptionContext);
  if (!context) throw new Error('useEncryption must be used within an EncryptionProvider');
  return context;
};

export const EncryptionProvider = ({ children, userId }) => {
  const [state, setState] = useState({ initialized: false, enabled: false, key: null, hasCloudKey: false, modal: false, policy: null, error: null, transition: null, pendingKey: null, pendingMode: null });
  const requestRef = useRef(0);
  const uidRef = useRef(userId || null);
  const localStateRef = useRef(null);
  uidRef.current = userId || null;
  const current = (uid, request) => uidRef.current === uid && requestRef.current === request;
  const patch = (uid, request, values) => { if (current(uid, request)) setState((old) => ({ ...old, ...values })); };

  useEffect(() => {
    let alive = true;
    const request = ++requestRef.current;
    const uid = userId;
    if (!uid) { localStateRef.current = null; setState({ initialized: true, enabled: false, key: null, hasCloudKey: false, modal: false, policy: null, error: null, transition: null, pendingKey: null, pendingMode: null }); return () => { alive = false; }; }
    setState({ initialized: false, enabled: false, key: null, hasCloudKey: false, modal: false, policy: null, error: null, transition: null, pendingKey: null, pendingMode: null });
    localStateRef.current = null;
    let local = null;
    let unsubscribe = () => {};
    let watchFailed = false;
    let snapshotGeneration = 0;
    const isCurrent = () => alive && current(uid, request);
    const initialize = async () => {
      try { local = await getStoredEncryptionState(uid); } catch (error) { logError('Error reading encryption state', error); }
      if (!isCurrent()) return;
      localStateRef.current = { uid, state: local };
      unsubscribe = onSnapshot(doc(db, 'users', uid), async (snapshot) => {
        if (!isCurrent() || watchFailed) return;
        const generation = ++snapshotGeneration;
        const isLatestSnapshot = () => isCurrent() && !watchFailed && generation === snapshotGeneration;
        let localState = localStateRef.current?.uid === uid ? localStateRef.current.state : null;
        const profile = snapshot.exists() ? snapshot.data() : {};
        const policy = readEncryptionPolicy(profile);
        const hasCloudKey = profileHasCloudKey(profile);
        if (policy.kind === 'invalid') { if (isLatestSnapshot()) patch(uid, request, { initialized: true, enabled: false, key: null, hasCloudKey, modal: false, policy, error: 'Invalid encryption policy.', transition: null, pendingKey: null, pendingMode: null }); return; }
        if (policy.kind === 'managed') {
          const locked = Boolean(policy.transition) || (policy.mode === 'enabled' && !localState?.key);
          if (isLatestSnapshot()) patch(uid, request, { initialized: true, enabled: policy.mode === 'enabled', key: localState?.key || null, hasCloudKey, modal: hasCloudKey && policy.mode === 'enabled' && !localState?.key, policy, error: locked && !policy.transition ? 'Encryption key is required.' : null, transition: policy.transition });
          return;
        }
        // Legacy is only allowed when the profile has no policy field whatsoever.
        if (!localState?.key) {
          try {
            const legacyKey = await getLegacyStoredEncryptionKey();
            if (legacyKey && isLatestSnapshot()) {
              const legacyNotes = await notesFor(uid);
              if (!isLatestSnapshot()) return;
              for (const note of legacyNotes.filter((item) => typeof item.content === 'string' && item.content.startsWith('encrypted:'))) await decryptData(note.content.slice('encrypted:'.length), legacyKey);
              if (!isLatestSnapshot()) return;
              if (legacyNotes.some((item) => typeof item.content === 'string' && item.content.startsWith('encrypted:'))) {
                const enabled = getLegacyEncryptionPreference() === true;
                if (await storeEncryptionState(uid, legacyKey, enabled, isLatestSnapshot) && isLatestSnapshot()) {
                  localState = { key: legacyKey, enabled };
                  localStateRef.current = { uid, state: localState };
                }
              }
            }
          } catch (error) { logError('Error migrating legacy encryption state', error); }
        }
        if (isLatestSnapshot()) patch(uid, request, { initialized: true, enabled: localState?.enabled === true, key: localState?.key || null, hasCloudKey, modal: hasCloudKey && !localState?.key, policy, error: null, transition: null });
      }, (error) => {
        watchFailed = true;
        snapshotGeneration += 1;
        logError('Error watching encryption policy', error);
        patch(uid, request, { initialized: true, enabled: false, key: null, hasCloudKey: false, modal: false, policy: { kind: 'unavailable' }, error: 'Unable to read encryption policy.', transition: null, pendingKey: null, pendingMode: null });
      });
    };
    initialize();
    return () => { alive = false; unsubscribe(); };
  }, [userId]);

  const canAct = () => userId && (state.policy?.kind === 'legacy' || state.policy?.kind === 'managed') && !state.transition;
  const persist = async (uid, request, key, enabled) => {
    const stored = await storeEncryptionState(uid, key, enabled, () => current(uid, request));
    if (stored && current(uid, request)) localStateRef.current = { uid, state: { key, enabled } };
    return stored;
  };
  const validateCloudKey = async (uid, request, key) => {
    if (state.policy?.kind === 'managed' && state.policy.mode === 'disabled') return { valid: true, sawEncrypted: false };
    const notes = await notesFor(uid);
    if (!current(uid, request)) return { valid: false, sawEncrypted: false };
    const requireEncrypted = state.policy?.kind === 'managed' && state.policy.mode === 'enabled';
    let sawEncrypted = false;
    for (const note of notes) {
      const encrypted = typeof note.content === 'string' && note.content.startsWith('encrypted:');
      if (requireEncrypted && !encrypted) return { valid: false, sawEncrypted };
      if (encrypted) {
        sawEncrypted = true;
        const plain = await decryptData(note.content.slice('encrypted:'.length), key);
        if (typeof plain !== 'string' || !current(uid, request)) return { valid: false, sawEncrypted };
      }
    }
    return { valid: true, sawEncrypted };
  };
  const migrate = async (uid, request, key, targetMode) => {
    const sourceMode = state.policy?.kind === 'managed' ? state.policy.mode : (state.enabled ? 'enabled' : 'disabled');
    const notes = await notesFor(uid);
    if (!current(uid, request)) throw new Error('Stale migration request.');
    return runEncryptionMigration({ notes, key, operationId: operationId(), sourceMode, targetMode, isCurrent: () => current(uid, request) });
  };
  const toggleEncryption = async () => {
    const uid = userId; const request = requestRef.current;
    if (!uid || !canAct()) return false;
    try {
      if (!state.enabled) {
        const notes = await notesFor(uid);
        if (!current(uid, request)) return false;
        if (notes.length > MAX_MIGRATION_NOTES) {
          patch(uid, request, { error: `Encryption can only be enabled for up to ${MAX_MIGRATION_NOTES} notes at a time. Your notes were not changed.` });
          return false;
        }
        const key = state.key || state.pendingKey || await generateEncryptionKey();
        if (!current(uid, request)) return false;
        // A key may never enable encryption until it is verified in cloud storage.
        patch(uid, request, { pendingKey: key, pendingMode: 'enabled', modal: true, error: null });
        return false;
      }
      if (!state.key) { patch(uid, request, { modal: true }); return false; }
      await migrate(uid, request, state.key, 'disabled');
      if (!current(uid, request)) return false;
      if (!await persist(uid, request, state.key, false)) return false;
      patch(uid, request, { enabled: false });
      return true;
    } catch (error) { logError('Error toggling encryption', error); patch(uid, request, { error: 'Unable to prepare encryption. Check your connection and try again.' }); return false; }
  };
  const saveKeyToCloud = async (password) => {
    const uid = userId; const request = requestRef.current; const key = state.pendingKey || state.key;
    if (!uid || !key || !canAct()) return false;
    try {
      if (!await storeAndVerifyKeyInCloud(uid, key, password) || !current(uid, request)) {
        patch(uid, request, { error: 'The cloud backup could not be verified. Check the password, connection, and encryption policy, then try again.' });
        return false;
      }
      const enabling = state.pendingMode === 'enabled';
      if (enabling) await migrate(uid, request, key, 'enabled');
      const enabled = enabling || (state.policy?.kind === 'managed' ? state.policy.mode === 'enabled' : state.enabled);
      if (!current(uid, request) || !await persist(uid, request, key, enabled)) return false;
      patch(uid, request, { key, pendingKey: null, pendingMode: null, enabled, modal: false });
      return true;
    } catch (error) { logError('Error saving cloud encryption key', error); patch(uid, request, { error: 'Unable to save the cloud backup. Check your connection and try again.' }); return false; }
  };
  const loadKeyFromCloud = async (password) => {
    const uid = userId; const request = requestRef.current;
    if (!uid || (state.policy?.kind !== 'legacy' && state.policy?.kind !== 'managed')) return false;
    try {
      const key = await getKeyFromCloud(uid, password);
      if (!key || !current(uid, request)) { patch(uid, request, { error: 'The cloud key could not be unlocked. Check the encryption password and try again.' }); return false; }
      const validation = await validateCloudKey(uid, request, key);
      if (!validation.valid || !current(uid, request)) { patch(uid, request, { error: 'The cloud key was opened but does not match the encrypted notes or current encryption policy.' }); return false; }
      const enabling = state.pendingMode === 'enabled';
      if (enabling) await migrate(uid, request, key, 'enabled');
      const enabled = enabling || (state.policy?.kind === 'managed' ? state.policy.mode === 'enabled' : (state.enabled || validation.sawEncrypted));
      if (!current(uid, request) || !await persist(uid, request, key, enabled)) return false;
      patch(uid, request, { key, pendingKey: null, pendingMode: null, enabled, modal: false, error: null });
      return true;
    } catch (error) { logError('Error loading cloud encryption key', error); patch(uid, request, { error: 'Unable to access the cloud key because of a technical or policy error. Try again later.' }); return false; }
  };
  const changeCloudKeyPassword = async (oldPassword, newPassword) => {
    if (!userId || !state.key || !canAct()) return false;
    try {
      const changed = Boolean(await changeKeyPassword(userId, state.key, oldPassword, newPassword));
      if (!changed) patch(userId, requestRef.current, { error: 'Password change was refused because the current password is incorrect, the cloud key does not match this browser\'s local key, or the backup could not be updated.' });
      return changed;
    } catch (error) { logError('Error changing cloud key password', error); patch(userId, requestRef.current, { error: 'Unable to change the cloud-key password because of a technical or policy error. Try again later.' }); return false; }
  };
  const abortActiveEncryptionTransition = async () => {
    const uid = userId; const request = requestRef.current; const operation = state.transition?.operationId;
    if (!uid || !operation) return false;
    try { await abortEncryptionTransition({ operationId: operation }); if (current(uid, request)) patch(uid, request, { transition: null }); return true; } catch (error) { logError('Error aborting encryption transition', error); return false; }
  };
  const verifyCloudKeyBackup = async (password) => {
    const uid = userId; const key = state.key;
    if (!uid || !key || !canAct() || typeof password !== 'string' || password.length === 0) return false;
    try { const recoveredKey = await getKeyFromCloud(uid, password); return Boolean(recoveredKey && await cryptoKeysEqual(key, recoveredKey)); }
    catch (_) { return false; }
  };

  if (!state.initialized) return null;
  const hasUsablePolicy = state.policy?.kind === 'legacy' || state.policy?.kind === 'managed';
  const isEncryptionLocked = !hasUsablePolicy || Boolean(state.transition) || (state.policy?.kind === 'managed' && state.policy.mode === 'enabled' && !state.key);
  return <EncryptionContext.Provider value={{ isEncryptionEnabled: state.enabled, encryptionKey: state.key, hasCloudKey: state.hasCloudKey, showCloudKeyModal: state.modal, setShowCloudKeyModal: (modal) => setState((old) => ({ ...old, modal })), toggleEncryption, saveKeyToCloud, loadKeyFromCloud, changeCloudKeyPassword, verifyCloudKeyBackup, encryptionPolicy: state.policy, encryptionError: state.error, activeEncryptionTransition: state.transition, encryptionTransition: state.transition, isEncryptionTransitioning: Boolean(state.transition), pendingEncryptionMode: state.pendingMode, isEncryptionLocked, abortActiveEncryptionTransition }}>{children}</EncryptionContext.Provider>;
};
